/**
 * Board 3 C — *When the office approves for the customer*, the company's
 * setting in Settings › Customers › Proposal (`docs/job-stages.md` §8 C).
 *
 * Who approved and when are always recorded; these three make the rest of
 * the office's Approve required: how it came in, what the customer said, and
 * proof. All off. Stored on `tenant_settings` (`approval_require_how`,
 * `_note`, `_proof`, migration 0162) through the page's own save
 * (`PUT /settings/proposal`); the office approve checks them again.
 *
 * The board's card: rounded 14, 14/18, the 14.5/700 title over its 12.5
 * muted sentence, then one row per box — 10 above and below, a `line-faint`
 * rule over each, the 18px box, the 13/700 name over its 11.5 muted line.
 */
import { cn } from '../lib/cn.js'
import { APPROVAL_SETTING } from './copy.js'
import { TickBox } from './popup.js'
import type { ApprovalRequires } from './rules.js'

const ROWS = ['how', 'note', 'proof'] as const satisfies readonly (keyof ApprovalRequires)[]

export function ApprovalSettingCard({ value, onChange, disabled = false }: {
  value: ApprovalRequires
  onChange: (next: ApprovalRequires) => void
  /** A viewer without *Company settings · edit* reads it and cannot change it. */
  disabled?: boolean
}) {
  return (
    <section
      data-as-drawn="js-03 C"
      aria-label={APPROVAL_SETTING.title}
      className="shrink-0 rounded-record border border-line bg-surface px-[18px] py-3.5 leading-[normal]"
    >
      <b className="text-[14.5px]">{APPROVAL_SETTING.title}</b>
      <div className="mb-2 mt-1 text-[12.5px] text-text-muted">{APPROVAL_SETTING.sub}</div>
      {ROWS.map((key) => {
        const on = value[key]
        const words = APPROVAL_SETTING[key]
        return (
          <button
            key={key}
            type="button"
            role="checkbox"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange({ ...value, [key]: !on })}
            className={cn('flex w-full items-start gap-[11px] border-t border-line-faint py-2.5 text-left tabular-nums',
              disabled && 'cursor-not-allowed')}
          >
            <TickBox on={on} />
            <span className="flex flex-col">
              <b className="text-[13px]">{words.label}</b>
              <span className="text-[11.5px] text-text-muted">{words.sub}</span>
            </span>
          </button>
        )
      })}
    </section>
  )
}
